import { useEffect } from 'react';

import { findMaterialByAttributes } from '../materials.utils';
import { useMaterials } from './useMaterials';
import { shouldReseedColorList } from './useSelectedMaterial.utils';

import { defaultColorSort } from '~/core/constants/sortAndFilter';
import { FiltersBySection } from '~/core/enums';
import { MaterialModel } from '~/core/models';
import { createSelectedHook } from '~/core/utils';
import {
  sortColorsByTrending,
  updateListAndFilters,
} from '~/core/utils/sortAndFilter';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectColorsByProductId, updateColorsIds } from '~/redux/slices/color';
import {
  selectRestorableMaterial,
  selectSelectedMaterial,
  updateSelectedMaterial,
} from '~/redux/slices/material';
import { updateFilters, updateSortBy } from '~/redux/slices/sortAndFilter';

const useMaterialsData = (): MaterialModel[] => {
  const { materials } = useMaterials();

  return materials;
};

const makeColorsSignature = (colors: MaterialModel['colors']) =>
  colors.map((c) => c.productId).join(',');

export const selectDefaultMaterial = ({
  materials,
  selectedValue,
}: {
  materials: MaterialModel[];
  selectedValue: Partial<MaterialModel> | undefined;
}) =>
  findMaterialByAttributes({
    materials,
    description: selectedValue?.description,
  }) ?? materials[0];

export const useSelectedMaterial = () => {
  const currentSelectedMaterial = useAppSelector(selectSelectedMaterial);
  const { selectedValue: selectedMaterial } = createSelectedHook({
    selector: selectSelectedMaterial,
    restorableSelector: selectRestorableMaterial,
    apiQueryHook: useMaterialsData,
    selectRestorableValue: ({ apiHookResponse, restorableValue }) =>
      findMaterialByAttributes({
        materials: apiHookResponse,
        description: restorableValue?.description,
      }),
    shouldReturnDefault: ({ selectedValue, apiHookResponse }) =>
      !selectedValue ||
      !apiHookResponse.find((material) => material.id === selectedValue?.id),
    selectDefaultValue: ({ apiHookResponse, selectedValue }) =>
      selectDefaultMaterial({ materials: apiHookResponse, selectedValue }),
  })();

  const dispatch = useAppDispatch();
  const currentColorsById = useAppSelector(selectColorsByProductId);

  const { materials } = useMaterials();

  useEffect(() => {
    if (materials.length === 0) {
      return;
    }

    // If createSelectedHook is returning a default/restored material while Redux is empty,
    // persist it to Redux so the stepper enables the next step (Color) immediately.
    if (!currentSelectedMaterial && selectedMaterial) {
      dispatch(updateSelectedMaterial(selectedMaterial));

      return;
    }

    if (materials.length > 0 && !selectedMaterial) {
      const defaultMaterial = materials[0];

      dispatch(updateSelectedMaterial(defaultMaterial));
    } else if (selectedMaterial) {
      const component = FiltersBySection.COLOR;
      const updatedMaterial =
        materials.find((m) => m.id === selectedMaterial?.id) ||
        selectedMaterial;

      const materialNeedsSync =
        updatedMaterial !== selectedMaterial &&
        updatedMaterial.id === selectedMaterial.id &&
        makeColorsSignature(updatedMaterial.colors) !==
          makeColorsSignature(selectedMaterial.colors);

      // Avoid dispatch loops: only update material when the selected record is invalid/stale
      if (materialNeedsSync) {
        dispatch(updateSelectedMaterial(updatedMaterial));
      }

      // Seed the color list, filters and default sort ONLY when the list in
      // Redux does not already match the selected material's colors (first load
      // or material change). Sorting/filtering only reorders/narrows
      // `color.usableIds` and never changes the set in `color.byId`, so this
      // stays false during those actions and no longer reverts them (SD-1280).
      if (shouldReseedColorList(currentColorsById, updatedMaterial.colors)) {
        const sortedColors = sortColorsByTrending(updatedMaterial.colors);
        const { filters, ids } = updateListAndFilters(sortedColors, {});
        const colorsById = sortedColors.reduce(
          (z, color) => ({ ...z, [color.productId]: color }),
          {}
        );

        dispatch(updateColorsIds({ byId: colorsById, usableIds: ids }));
        dispatch(updateFilters({ component, filters, selectedFilters: {} }));
        dispatch(updateSortBy({ component, sortBy: defaultColorSort }));
      }
    }
  }, [
    selectedMaterial,
    materials,
    dispatch,
    currentColorsById,
    currentSelectedMaterial,
  ]);

  return selectedMaterial;
};
